import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { BlogMarkdown } from './AdminBlogEditor';
import { SocialShare } from '../components/SocialShare';
import {
  Calendar, ArrowLeft, ArrowRight, Loader2, AlertCircle, User as UserIcon, TrendingUp,
} from 'lucide-react';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('de-CH', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
const readingTime = (body) => `${Math.max(1, Math.ceil((body || '').split(/\s+/).length / 200))} Min. Lesezeit`;

export const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(apiBase() + `/api/blog/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error('not-found');
        return r.json();
      })
      .then((d) => setPost(d.post))
      .catch(() => setError(true))
      .finally(() => setLoading(false));

    // Related (latest 3 published, excluding current slug)
    fetch(apiBase() + '/api/blog?limit=4')
      .then((r) => r.json())
      .then((d) => setRelated((d.posts || []).filter((p) => p.slug !== slug).slice(0, 3)))
      .catch(() => {});
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#C4A55A]" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SEO title="Beitrag nicht gefunden" noindex />
        <Header />
        <main className="pt-32 pb-24 container mx-auto px-4 max-w-3xl text-center">
          <AlertCircle className="h-12 w-12 text-[#C4A55A] mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2">Beitrag nicht gefunden</h1>
          <p className="text-gray-400 mb-6">Dieser Artikel existiert nicht (mehr) oder ist noch nicht veröffentlicht.</p>
          <button onClick={() => navigate('/blog')} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C4A55A]/15 border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/25">
            <ArrowLeft className="h-4 w-4" /> Zurück zum Blog
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const canonical = `https://loewenherz.ag/blog/${post.slug}`;

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title={post.metaTitle || post.title}
        description={post.metaDescription || post.excerpt}
        image={post.coverImageUrl}
        type="article"
        keywords={(post.tags || []).join(', ')}
        breadcrumb={[
          { name: 'Home', url: 'https://loewenherz.ag/' },
          { name: 'Blog', url: 'https://loewenherz.ag/blog' },
          { name: post.title, url: canonical },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.metaDescription || post.excerpt,
          image: post.coverImageUrl || undefined,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt || post.publishedAt,
          author: {
            '@type': 'Organization',
            name: post.authorName || 'Löwenherz Invest AG',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Löwenherz Invest AG',
            logo: { '@type': 'ImageObject', url: 'https://loewenherz.ag/logo.png' },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
          keywords: (post.tags || []).join(', '),
        }}
      />

      <Header />

      <main className="pt-28 pb-24">
        <article className="container mx-auto px-4 max-w-3xl">
          {/* Breadcrumb */}
          <nav className="text-xs text-gray-500 mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#C4A55A]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-[#C4A55A]">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-400">{post.title}</span>
          </nav>

          {/* Tags */}
          {(post.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((t) => (
                <Link
                  key={t}
                  to={`/blog?tag=${encodeURIComponent(t)}`}
                  className="text-[10px] uppercase tracking-wider text-[#C4A55A] bg-[#C4A55A]/10 border border-[#C4A55A]/20 px-2 py-0.5 rounded-full hover:bg-[#C4A55A]/20"
                >
                  {t}
                </Link>
              ))}
            </div>
          )}

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4" data-testid="blog-detail-title">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-lg text-gray-400 italic mb-6 leading-relaxed">{post.excerpt}</p>
          )}

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-8 pb-8 border-b border-white/10">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {fmtDate(post.publishedAt || post.createdAt)}
            </span>
            <span className="inline-flex items-center gap-1">
              <UserIcon className="h-3 w-3" /> {post.authorName || 'Löwenherz Invest AG'}
            </span>
            <span>{readingTime(post.body)}</span>
            {post.viewCount ? <span>{post.viewCount} Aufrufe</span> : null}
          </div>

          {/* Cover */}
          {post.coverImageUrl && (
            <img
              src={post.coverImageUrl} alt={post.title}
              className="w-full rounded-2xl border border-white/10 mb-8"
            />
          )}

          {/* Body */}
          <BlogMarkdown body={post.body} />

          {/* CTA */}
          <div className="mt-12 bg-gradient-to-br from-[#C4A55A]/15 via-[#0f0f0f] to-black border border-[#C4A55A]/30 rounded-2xl p-8" data-testid="blog-cta">
            <div className="flex items-center gap-2 text-[#C4A55A] mb-2">
              <TrendingUp className="h-5 w-5" />
              <span className="text-xs uppercase tracking-wider">Werden Sie Aktionär</span>
            </div>
            <h3 className="text-2xl font-bold mb-2">Investor werden – in drei Schritten</h3>
            <p className="text-gray-300 leading-relaxed mb-5">
              Sichern Sie sich heute Ihre Beteiligung an Schweizer Premium-Immobilien.
              Registrieren – verifizieren – zeichnen.
            </p>
            <Link
              to={`/?ref=blog-${post.slug}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] text-black font-semibold hover:opacity-90 transition"
              data-testid="blog-cta-btn"
            >
              Jetzt investieren <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Share */}
          <div className="mt-10">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-3">Beitrag teilen</div>
            <SocialShare size="md" url={canonical} title={post.title} />
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-16 pt-10 border-t border-white/10">
              <h3 className="text-2xl font-bold mb-6">Weitere Beiträge</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.id} to={`/blog/${r.slug}`}
                    className="bg-[#0f0f0f] border border-white/10 rounded-xl overflow-hidden hover:border-[#C4A55A]/40 transition-colors group"
                  >
                    {r.coverImageUrl && (
                      <img src={r.coverImageUrl} alt={r.title} className="w-full aspect-[16/9] object-cover" loading="lazy" />
                    )}
                    <div className="p-4">
                      <h4 className="font-semibold group-hover:text-[#C4A55A] transition-colors line-clamp-2">{r.title}</h4>
                      <div className="text-xs text-gray-500 mt-2">{fmtDate(r.publishedAt || r.createdAt)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
};
